import { css } from 'lit';
/* eslint-disable max-lines */
import get from 'lodash-es/get';
import HTTPMethod from 'http-method-enum';
import { Action } from 'redux';
import debounce from 'lodash-es/debounce';
import { ThunkDispatch } from 'redux-thunk/es/types';
import isSvg from 'is-svg';
import { GeoJSONGeometry, stringify } from 'wellknown';
import { EventTypeConfig } from '@/config/ConfigREMEventTypes';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { MultiLineString } from 'geojson';
import { center, nearestPointOnLine } from '@turf/turf';
import { LocationDto, LocationTypes, RoadEventAttachmentDto } from '@typings/api';
import {
	projectContactIsEmptyAndOptional,
	projectContactIsValidForWorkZone,
} from '@/components/wzrt/work-zone-validation';
import {
	CreatePreviewDto,
	DraftEvent,
	EventStatus,
	PreviewDto,
	PublishActionDto,
	PublishActionType,
	QueueState,
	RespondingUnitDto,
	RoadEventDto,
	RoadEventFieldDto,
	RoadEventTimelineDto,
	RoadwayEventMessagesDto,
	SignDto,
	UserPermissions,
	getEmptyLaneBlockage,
} from '../../../typings/api';
import {
	APIErrorJSON,
	DotNotationPath,
	NotificationErrorType,
} from '../../../typings/shared-types';
import ConfigCARSx, { APIConfig, DebuggingConfig, Districts } from '../../config/ConfigCARSx';
import {
	CARS_EVENT_INTEGRATION_ENABLED,
	ConfigREMApi,
	ConfigREMEventForm,
	DRAFT_PROP_PATHS_THAT_INVALIDATE_DESCRIPTION,
	DRAFT_PROP_PATHS_THAT_INVALIDATE_DMS_PREVIEW,
	DRAFT_PROP_PATHS_THAT_INVALIDATE_ICON,
	DRAFT_PROP_PATHS_THAT_INVALIDATE_LOCATION_DETAILS,
	FUTURE_EVENT_ICON_THRESHOLD,
	REM_FORM_SECTIONS,
	getEmptyDraftEvent,
} from '../../config/ConfigREM';
import { AppSection, EMAIL_ADDRESS_REGEX, HttpStatusCode, REMView } from '../../constants';
import { isAPIErrorJSON } from '../../utils/type-guards';
import { isValidNumber, isValidString } from '../../utils/utils';
import _isNotEqual from '../../utils/_isNotEqual';
import APIRequest, { APIError, APIRequestReturn, getAPIHeaders } from '../APIRequest';
import { addItem, removeItem } from '../redux-loaderAnim';
import { navigate, navigateBack } from '../redux-routing';
import store, { RootAction, RootState, ThunkActionRoot } from '../redux-store';
import { showMainBanner } from '../redux-ui';
import { REMActionType } from './rem-actions';
import {
	getDMSforREMEvent,
	getInitialSignSelectionStateFromDMSMessages,
	getSignsByIds,
	prepareDMSMessagesForClient,
	prioritizeMessages,
	setDMSPreview,
	setDMSSignLoadingPreviewIds,
} from './rem-actions-event-dms';
import {
	handleNewLocationDetailsForREMEvent,
	setDraftEventGeometry,
} from './rem-actions-event-location';
import { setREMEventTimeline } from './rem-actions-event-timeline';
import {
	selectDefaultLaneBlockgeType,
	selectHasSufficientDataFor511Import,
	selectHasSufficientDataForDMSPreview,
	selectHasSufficientDataForDescription,
	selectHasSufficientDataForLocationDetails,
	selectRouteByName,
} from './rem-selectors';
import REMState from './rem-state';
import OverrideProps from '../../utils/OverrideProps';
import userHasPermission from '../user-permissions';
import Authorization from '../../rest/Authorization';
import { DRAFT_PROP_PATHS_THAT_INVALIDATE_SIGNS, FormSectionConfig } from '../../config/ConfigREM';
import { ConfigWZRT } from '../../config/ConfigWZRT';
import { userCanPerformWorkZoneAction } from '../user-permissions';
import { selectRampById } from './rem-selectors';

type PreparedLocalDraftEventMod = {
	nearbyCameras: undefined;
	lat?: number;
	lon?: number;
};

type PreparedLocalDraftEvent = OverrideProps<DraftEvent, PreparedLocalDraftEventMod>;

export const prepareDraftEventForServer = (draftEvent: DraftEvent): PreparedLocalDraftEvent => ({
	...draftEvent,
	//	vehicles whose details have yet to be filled out are allowed on the front-end, but break the backend
	vehicles: draftEvent.vehicles?.filter(
		(vehicle) => isValidString(vehicle.licensePlate) || isValidString(vehicle.description),
	),
	//	responding units without a unitType and disposition are allowed on the front-end, but break the backend
	respondingUnits: draftEvent.respondingUnits?.filter((respondingUnit) =>
		isValidString(respondingUnit.unitType),
	),
	//	strip out empty lane blockage entries - they break automatic DMS messaging generation
	positiveLaneBlockage: _isNotEqual(draftEvent.positiveLaneBlockage, getEmptyLaneBlockage())
		? draftEvent.positiveLaneBlockage
		: getEmptyLaneBlockage(),
	negativeLaneBlockage: _isNotEqual(draftEvent.negativeLaneBlockage, getEmptyLaneBlockage())
		? draftEvent.negativeLaneBlockage
		: getEmptyLaneBlockage(),
	//	these are always defined by the server, the frontend can't change them
	nearbyCameras: undefined,
	geometry: draftEvent.eventLocation === LocationTypes.LOCAL_ROAD ? draftEvent.geometry : undefined,
	lon: draftEvent.eventLocation === LocationTypes.LOCAL_ROAD ? draftEvent.lon : undefined,
	lat: draftEvent.eventLocation === LocationTypes.LOCAL_ROAD ? draftEvent.lat : undefined,
	selectedSigns: draftEvent.selectedSigns,
});

const signSelectionHasMessageOverride = (
	selectedSign: NonNullable<DraftEvent['selectedSigns']>[number],
): boolean =>
	!!(
		selectedSign.message?.frame1?.messageOverride ||
		selectedSign.message?.frame2?.messageOverride ||
		selectedSign.message?.frame3?.messageOverride
	);

export const prepareDraftEventForPreviewCreation = (
	event: DraftEvent,
): Partial<CreatePreviewDto> => ({
	selectedSigns: !userHasPermission(UserPermissions.REM_CAN_CREATE_EVENTS_LIMITED)
		? event.selectedSigns?.map((selectedSign) => {
				if (signSelectionHasMessageOverride(selectedSign)) {
					return selectedSign;
				}
				return {
					...selectedSign,
					message: {
						...selectedSign.message,
						frame1: undefined,
						frame2: undefined,
						frame3: undefined,
						messageType: undefined,
					},
				};
			})
		: [],
	eventType: event.eventType,
	route: event.route,
	startMileMarker: event.startMileMarker,
	endMileMarker: event.endMileMarker,
	positiveLaneBlockage: _isNotEqual(event.positiveLaneBlockage, getEmptyLaneBlockage())
		? event.positiveLaneBlockage
		: getEmptyLaneBlockage(),
	positiveLaneBlockageType: event.positiveLaneBlockageType,
	negativeLaneBlockage: _isNotEqual(event.negativeLaneBlockage, getEmptyLaneBlockage())
		? event.negativeLaneBlockage
		: getEmptyLaneBlockage(),
	negativeLaneBlockageType: event.negativeLaneBlockageType,
	startTime: event.dateStart,
	eventId: event.id,
	rampPk: event.rampPk,
	eventLocation: event.eventLocation as LocationTypes,
});

export const prepareServerEventForClient = (event: RoadEventDto): DraftEvent => {
	const defaultLaneBlockageType = selectDefaultLaneBlockgeType(store.getState());

	const positiveLaneBlockage = event.positiveLaneBlockage ?? getEmptyLaneBlockage();
	positiveLaneBlockage.lanesAffected = positiveLaneBlockage.lanesAffected ?? [];
	const positiveLaneBlockageType = event.positiveLaneBlockageType ?? defaultLaneBlockageType;

	const negativeLaneBlockage = event.negativeLaneBlockage ?? getEmptyLaneBlockage();
	negativeLaneBlockage.lanesAffected = negativeLaneBlockage.lanesAffected ?? [];
	const negativeLaneBlockageType = event.negativeLaneBlockageType ?? defaultLaneBlockageType;

	return {
		...event,
		positiveLaneBlockage,
		positiveLaneBlockageType,
		negativeLaneBlockage,
		negativeLaneBlockageType,
		selectedSigns: event.selectedSigns === null ? undefined : event.selectedSigns,
		emailEnabled: ConfigREMEventForm.notifications.emailEnabledDefault,
	};
};

export type GetREMEvents = Action<typeof REMActionType.GET_REM_EVENTS>;
export interface SetREMEvents extends Action<typeof REMActionType.SET_REM_EVENTS> {
	events: REMState['events'];
}
export const setREMEvents = (events: RoadEventDto[]): SetREMEvents => ({
	type: REMActionType.SET_REM_EVENTS,
	events,
});

export type GetREMEventImports = Action<typeof REMActionType.GET_REM_EVENT_IMPORTS>;
export interface SetREMEventImports extends Action<typeof REMActionType.SET_REM_EVENT_IMPORTS> {
	events: REMState['eventImports'];
}

export type GetWorkZoneRequests = Action<typeof REMActionType.GET_WORK_ZONE_REQUESTS>;
export interface SetWorkZoneRequests extends Action<typeof REMActionType.SET_WORK_ZONE_REQUESTS> {
	events: REMState['workZoneRequests'];
}

export type ForgetREMEvent = Action<typeof REMActionType.FORGET_REM_EVENT>;
export type TakeoverREMEvent = Action<typeof REMActionType.TAKEOVER_REM_EVENT>;

export interface GetREMEventFromEvents extends Action<
	typeof REMActionType.GET_REM_EVENT_FROM_EVENTS
> {
	eventId: number;
}

export interface GetREMEvent extends Action<typeof REMActionType.GET_REM_EVENT> {
	eventId: number;
}

export interface SetREMEvent extends Action<typeof REMActionType.SET_REM_EVENT> {
	event: RoadEventDto;
}

export type GetREMEventFields = Action<typeof REMActionType.GET_REM_EVENT_FIELDS>;

export interface SetREMEventFields extends Action<typeof REMActionType.SET_REM_EVENT_FIELDS> {
	eventFields: REMState['eventFields'];
}

export const setREMEventFields = (eventFields: REMState['eventFields']): SetREMEventFields => ({
	type: REMActionType.SET_REM_EVENT_FIELDS,
	eventFields,
});

export interface CreateREMEvent extends Action<typeof REMActionType.CREATE_REM_EVENT> {
	draftEvent: REMState['draftEvent'];
}
export interface SetREMDraftEvent extends Action<typeof REMActionType.SET_REM_DRAFT_EVENT> {
	draftEvent: REMState['draftEvent'];
}

export interface UpdateREMEvent extends Action<typeof REMActionType.UPDATE_REM_EVENT> {
	draftEvent: REMState['draftEvent'];
}

export interface SetDraftEventProp extends Action<typeof REMActionType.SET_REM_DRAFT_EVENT_PROP> {
	path: string;
	value: unknown;
}

export type REMEventUpdated = Action<typeof REMActionType.REM_EVENT_UPDATED>;

export interface ShowREMEventSection extends Action<typeof REMActionType.SHOW_REM_EVENT_SECTION> {
	section: keyof typeof REM_FORM_SECTIONS;
}

export interface HideREMEventSection extends Action<typeof REMActionType.HIDE_REM_EVENT_SECTION> {
	section: keyof typeof REM_FORM_SECTIONS;
}

export interface SetREMReadOnlyMode extends Action<typeof REMActionType.SET_REM_READ_ONLY_MODE> {
	readOnlyMode: REMState['readOnlyMode'];
}

export interface SetREMLoading extends Action<typeof REMActionType.SET_REM_LOADING> {
	loading: REMState['loading'];
}

export interface SetDispositionError extends Action<typeof REMActionType.SET_DISPOSITION_ERROR> {
	dispositionError: REMState['dispositionError'];
}

export interface GetREMEventDescription extends Action<
	typeof REMActionType.GET_REM_DRAFT_EVENT_DESCRIPTION_PREVIEW
> {
	event: RoadEventDto;
}

export interface SetREMEventDescription extends Action<
	typeof REMActionType.SET_REM_DRAFT_EVENT_DESCRIPTION_PREVIEW
> {
	description?: string;
}

export interface SetREMUserHasModifiedIncludeInEventFeed extends Action<
	typeof REMActionType.SET_REM_USER_HAS_MODIFIED_INCLUDE_IN_EVENT_FEED
> {
	modified: boolean;
}

export const setREMUserHasModifiedIncludeInEventFeed = (
	modified: boolean,
): SetREMUserHasModifiedIncludeInEventFeed => ({
	type: REMActionType.SET_REM_USER_HAS_MODIFIED_INCLUDE_IN_EVENT_FEED,
	modified,
});

export const setREMEventDescription = (description?: string): SetREMEventDescription => ({
	type: REMActionType.SET_REM_DRAFT_EVENT_DESCRIPTION_PREVIEW,
	description,
});

export interface GetREMDraftEventIcon extends Action<
	typeof REMActionType.GET_REM_DRAFT_EVENT_ICON
> {
	event: RoadEventDto;
}

export interface SetREMDraftEventIcon extends Action<
	typeof REMActionType.SET_REM_DRAFT_EVENT_ICON
> {
	icon?: string;
}

export interface SetUnsavedDraftEvent extends Action<typeof REMActionType.SET_UNSAVED_DRAFT_EVENT> {
	unsavedDraftEvent: boolean;
}

export const setUnsavedDraftEvent = (unsavedDraftEvent: boolean): SetUnsavedDraftEvent => ({
	type: REMActionType.SET_UNSAVED_DRAFT_EVENT,
	unsavedDraftEvent,
});

export const setREMDraftEventIcon = (icon?: string): SetREMDraftEventIcon => ({
	type: REMActionType.SET_REM_DRAFT_EVENT_ICON,
	icon,
});

export interface SetREMDraftEventFormError extends Action<
	typeof REMActionType.SET_REM_DRAFT_EVENT_FORM_ERROR
> {
	field: string;
	error?: string;
}

export const setREMDraftEventFormError = (
	field: string,
	error?: string,
): SetREMDraftEventFormError => ({
	type: REMActionType.SET_REM_DRAFT_EVENT_FORM_ERROR,
	field,
	error,
});

export interface SetREMDraftEventFormErrors extends Action<
	typeof REMActionType.SET_REM_DRAFT_EVENT_FORM_ERRORS
> {
	errors: Record<string, string>;
}

export const setREMDraftEventFormErrors = (
	errors: Record<string, string>,
): SetREMDraftEventFormErrors => ({
	type: REMActionType.SET_REM_DRAFT_EVENT_FORM_ERRORS,
	errors,
});

export interface SetWorkZoneTableStateString extends Action<
	typeof REMActionType.SET_WORK_ZONE_TABLE_STATE_STRING
> {
	tableStateString?: string | null;
}

export const setWorkZoneTableStateString = (
	tableStateString?: string | null,
): SetWorkZoneTableStateString => ({
	type: REMActionType.SET_WORK_ZONE_TABLE_STATE_STRING,
	tableStateString,
});

export interface SetREMDraftEventAttachments extends Action<
	typeof REMActionType.SET_REM_DRAFT_EVENT_ATTACHMENTS
> {
	attachments: RoadEventAttachmentDto[];
}

export const setREMDraftEventAttachments = (
	attachments: RoadEventAttachmentDto[],
): SetREMDraftEventAttachments => ({
	type: REMActionType.SET_REM_DRAFT_EVENT_ATTACHMENTS,
	attachments,
});

export interface SetREMDraftEventAttachmentsFileMap extends Action<
	typeof REMActionType.SET_REM_DRAFT_EVENT_ATTACHMENTS_FILE_MAP
> {
	fileMap: Record<number, File>;
}

export const setREMDraftEventAttachmentsFileMap = (
	fileMap: Record<number, File>,
): SetREMDraftEventAttachmentsFileMap => ({
	type: REMActionType.SET_REM_DRAFT_EVENT_ATTACHMENTS_FILE_MAP,
	fileMap,
});

export type ClearDraftEventState = Action<typeof REMActionType.CLEAR_DRAFT_EVENT_STATE>;

const debouncedGetDMSforREMEvent = debounce(() => {
	store.dispatch(setDMSSignLoadingPreviewIds([]));
	const state = store.getState();
	if (state.rem.draftEvent !== undefined) {
		return store.dispatch(getDMSforREMEvent(state.rem.draftEvent, true));
	}
	return undefined;
}, 100);

export const getEventDescription =
	(event: Partial<RoadEventDto>): ThunkActionRoot<Promise<APIRequestReturn | void>> =>
	async (dispatch): Promise<APIRequestReturn | void> => {
		dispatch({
			type: REMActionType.GET_REM_DRAFT_EVENT_DESCRIPTION_PREVIEW,
		});

		if (!CARS_EVENT_INTEGRATION_ENABLED) {
			return;
		}

		const url = APIConfig.eventPreviewAPI;
		const processedEvent = { ...event };

		if (!isValidNumber(processedEvent.startMileMarker)) {
			delete processedEvent.startMileMarker;
			delete processedEvent.geometry;
		}

		const apiRequestReturn = await APIRequest(
			new Request(url, {
				method: HTTPMethod.POST,
				body: JSON.stringify(processedEvent),
				headers: {
					'Content-Type': 'application/json',
				},
			}),
			APIConfig.requestTimeoutMs,
			false,
		);

		try {
			const description: { eventPreview: string } = (await apiRequestReturn.response
				?.clone()
				.json()) as { eventPreview: string };

			dispatch(setREMEventDescription(description.eventPreview));
		} catch (error) {
			apiRequestReturn.apiError = APIError.ResponseUnparseable;
			if (DebuggingConfig.showConsoleLogs) {
				console.error(`error getting description for event: `, error);
			}
		}
	};

export interface SetREMDraftEventFutureIcon extends Action<REMActionType.SET_REM_DRAFT_EVENT_FUTURE_ICON> {
	futureIcon?: string;
}

export const setREMDraftEventFutureIcon = (futureIcon?: string): SetREMDraftEventFutureIcon => ({
	type: REMActionType.SET_REM_DRAFT_EVENT_FUTURE_ICON,
	futureIcon,
});

export interface SetREMEventIcons extends Action<REMActionType.SET_REM_EVENT_ICONS> {
	icons: Record<number, { current?: string; future?: string }>;
}

export const setREMEventIcons = (
	icons: Record<number, { current?: string; future?: string }>,
): SetREMEventIcons => ({
	type: REMActionType.SET_REM_EVENT_ICONS,
	icons,
});

export interface SetREMEventIconsLoading extends Action<REMActionType.SET_REM_EVENT_ICONS_LOADING> {
	loading: boolean;
}

export const setREMEventIconsLoading = (loading: boolean): SetREMEventIconsLoading => ({
	type: REMActionType.SET_REM_EVENT_ICONS_LOADING,
	loading,
});

export type REMEventIconRequest = {
	id: number;
	dateStart?: number;
	future?: boolean;
};

const userTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

const eventNeedsFutureIcon = (event: REMEventIconRequest): boolean => {
	if (event.future !== undefined) {
		return event.future;
	}

	return (
		isValidNumber(event.dateStart) && event.dateStart - Date.now() > FUTURE_EVENT_ICON_THRESHOLD
	);
};

const inFlightCurrentIconIds = new Set<number>();
const inFlightFutureIconIds = new Set<number>();

const fetchBulkEventIcons = async (
	requests: { eventId: number; iconType?: string; timezone?: string }[],
): Promise<Record<string, string> | null> => {
	if (requests.length === 0) {
		return {};
	}

	const url = new URL(ConfigREMApi.eventIconsBulk(), APIConfig.endpointURLBase);
	const apiRequestReturn = await APIRequest(
		new Request(url.href, {
			method: HTTPMethod.POST,
			headers: new Headers({
				...getAPIHeaders(),
			}),
			body: JSON.stringify(requests),
		}),
	);

	if (apiRequestReturn.response?.ok !== true) {
		return null;
	}

	try {
		return (await apiRequestReturn.response.json()) as Record<string, string>;
	} catch (error) {
		if (DebuggingConfig.showConsoleLogs) {
			console.error(`error parsing bulk event icons:`, error);
		}
		return null;
	}
};

const svgFromBulkResponse = (icons: Record<string, string>, eventId: number): string => {
	const icon = icons[eventId] ?? icons[String(eventId)] ?? '';
	return isSvg(icon) ? icon : '';
};

export const getREMEventIcons =
	(events: REMEventIconRequest[]): ThunkActionRoot<Promise<void>> =>
	async (dispatch, getState): Promise<void> => {
		const cached = getState().rem.eventIcons;
		const currentIds: number[] = [];
		const futureIds: number[] = [];

		events.forEach((event) => {
			if (!isValidNumber(event.id)) {
				return;
			}

			if (eventNeedsFutureIcon(event)) {
				if (cached[event.id]?.future === undefined && !inFlightFutureIconIds.has(event.id)) {
					futureIds.push(event.id);
					inFlightFutureIconIds.add(event.id);
				}
			} else if (cached[event.id]?.current === undefined && !inFlightCurrentIconIds.has(event.id)) {
				currentIds.push(event.id);
				inFlightCurrentIconIds.add(event.id);
			}
		});

		if (currentIds.length === 0 && futureIds.length === 0) {
			return;
		}

		const timezone = userTimeZone();
		dispatch(setREMEventIconsLoading(true));

		try {
			const [currentIcons, futureIcons] = await Promise.all([
				fetchBulkEventIcons(currentIds.map((eventId) => ({ eventId }))),
				fetchBulkEventIcons(
					futureIds.map((eventId) => ({
						eventId,
						iconType: 'future',
						timezone,
					})),
				),
			]);

			const icons: Record<number, { current?: string; future?: string }> = {};

			if (currentIcons) {
				currentIds.forEach((eventId) => {
					icons[eventId] = {
						...icons[eventId],
						current: svgFromBulkResponse(currentIcons, eventId),
					};
				});
			}

			if (futureIcons) {
				futureIds.forEach((eventId) => {
					icons[eventId] = {
						...icons[eventId],
						future: svgFromBulkResponse(futureIcons, eventId),
					};
				});
			}

			if (Object.keys(icons).length > 0) {
				dispatch(setREMEventIcons(icons));
			}
		} finally {
			dispatch(setREMEventIconsLoading(false));
			currentIds.forEach((eventId) => inFlightCurrentIconIds.delete(eventId));
			futureIds.forEach((eventId) => inFlightFutureIconIds.delete(eventId));
		}
	};

export const getDraftEventIcon =
	(event: DraftEvent): ThunkActionRoot<Promise<APIRequestReturn | null>> =>
	async (dispatch): Promise<APIRequestReturn | null> => {
		if (event.eventType == null) {
			dispatch(setREMDraftEventIcon(undefined));
			dispatch(setREMDraftEventFutureIcon(undefined));

			return null;
		}

		dispatch({ type: REMActionType.GET_REM_DRAFT_EVENT_ICON });

		const url = new URL(ConfigREMApi.eventIconBase(), APIConfig.endpointURLBase);

		const defaultIconRequestReturn = await APIRequest(
			new Request(url.href, {
				method: HTTPMethod.POST,
				headers: new Headers({
					'Authorization': Authorization.JWT ?? '',
					'Content-Type': 'application/json',
				}),
				body: JSON.stringify(event),
			}),
		);

		if (defaultIconRequestReturn.response?.status === 401) {
			void dispatch(navigate(ConfigCARSx.Pages[AppSection.LOGIN].route));
		}
		if (defaultIconRequestReturn.response?.status === 404) {
			dispatch(
				showMainBanner(NotificationErrorType.ERROR, {
					title: `Error occured when fetching event icon`,
				}),
			);
			return defaultIconRequestReturn;
		}
		if (defaultIconRequestReturn.response?.status === 500) {
			dispatch(
				showMainBanner(NotificationErrorType.ERROR, {
					title: `Server error occured when fetching event icon`,
				}),
			);
			return defaultIconRequestReturn;
		}
		if (defaultIconRequestReturn.response?.ok === false) {
			return defaultIconRequestReturn;
		}
		try {
			const icon: string = (await defaultIconRequestReturn.response?.text()) ?? '';

			if (isSvg(icon)) {
				dispatch(setREMDraftEventIcon(icon));
			} else {
				dispatch(
					showMainBanner(NotificationErrorType.ERROR, {
						title: `Server returned invalid SVG string for event icon of type: ${
							event.eventType ?? 'null'
						}`,
					}),
				);
			}

			let futureIcon;
			const shouldRequestFutureIcon =
				event.dateStart && event.dateStart - new Date().getTime() > FUTURE_EVENT_ICON_THRESHOLD;

			if (shouldRequestFutureIcon) {
				url.searchParams.append('iconType', 'future');
				url.searchParams.append('timezone', userTimeZone());
				const futureIconRequestReturn = await APIRequest(
					new Request(url.href, {
						method: HTTPMethod.POST,
						headers: new Headers({
							'Authorization': Authorization.JWT ?? '',
							'Content-Type': 'application/json',
						}),
						body: JSON.stringify(event),
					}),
				);
				futureIcon = (await futureIconRequestReturn.response?.text()) as string;

				if (!isSvg(futureIcon)) {
					futureIcon = undefined;

					dispatch(
						showMainBanner(NotificationErrorType.ERROR, {
							title: `Server returned invalid SVG string for future event icon of type: ${
								event.eventType ?? 'null'
							}`,
						}),
					);
				}
			}
			dispatch(setREMDraftEventFutureIcon(futureIcon));
		} catch (error) {
			defaultIconRequestReturn.apiError = APIError.ResponseUnparseable;
			if (DebuggingConfig.showConsoleLogs) {
				console.error(`error fetching icon for draft event:`, error);
			}
		}

		return defaultIconRequestReturn;
	};

const updateDraftIcon = async (
	dispatch: ThunkDispatch<RootState, void, RootAction>,
	getState: () => RootState,
	path: string,
): Promise<void> => {
	const event = getState().rem.draftEvent;

	if (
		// event?.eventType != null &&
		DRAFT_PROP_PATHS_THAT_INVALIDATE_ICON.includes(path) &&
		event !== undefined
	) {
		if (DebuggingConfig.showConsoleLogs) {
			console.warn(`icon refreshed due to: ${path}`);
		}

		if (event?.eventType) {
			await dispatch(getDraftEventIcon(event));
		} else {
			dispatch(setREMDraftEventIcon(undefined));
			dispatch(setREMDraftEventFutureIcon(undefined));
		}
	}
};

export const tryRefreshDMSPreviews = async (
	dispatch: ThunkDispatch<RootState, void, RootAction>,
	getState: () => RootState,
	path: string,
) => {
	const state = getState();
	if (
		DRAFT_PROP_PATHS_THAT_INVALIDATE_SIGNS.includes(path) &&
		selectHasSufficientDataForDMSPreview(state)
	) {
		// eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
		void dispatch(setREMDraftEventProp('selectedSigns', []));
	}
	if (
		DRAFT_PROP_PATHS_THAT_INVALIDATE_DMS_PREVIEW.includes(path) &&
		selectHasSufficientDataForDMSPreview(state)
	) {
		// eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
		clearDMSPreviews();

		await debouncedGetDMSforREMEvent();
	}
};

const updateDraftLocationDetails = async (
	dispatch: ThunkDispatch<RootState, void, RootAction>,
	getState: () => RootState,
	path: string,
): Promise<void> => {
	const event = getState().rem.draftEvent;
	const state = getState();

	if (
		event !== undefined &&
		DRAFT_PROP_PATHS_THAT_INVALIDATE_LOCATION_DETAILS.includes(path) &&
		state.rem.mileMarkersValidForRoute === true &&
		selectHasSufficientDataForLocationDetails(state)
	) {
		if (DebuggingConfig.showConsoleLogs) {
			console.warn(`location details refreshed due to: ${path}`);
		}

		const { route, startMileMarker, endMileMarker } = event;
		await handleNewLocationDetailsForREMEvent(
			dispatch,
			route,
			startMileMarker,
			endMileMarker,
			event.eventLocation as LocationTypes | undefined,
		);
	}
};

const updateDraftDescription = (
	dispatch: ThunkDispatch<RootState, void, RootAction>,
	getState: () => RootState,
	path: string,
): void => {
	const state = getState();
	const event = state.rem.draftEvent;
	const geometry = state.rem.draftEventGeometry;

	if (
		DRAFT_PROP_PATHS_THAT_INVALIDATE_DESCRIPTION.includes(path) &&
		selectHasSufficientDataForDescription(state) &&
		event !== undefined &&
		event.eventType !== undefined
	) {
		if (DebuggingConfig.showConsoleLogs) {
			console.warn(`description preview refreshed due to: ${path}`);
		}
		// if user has modified this value, leave unchanged, otherwise set to default configured value for the event type
		const includeInEventFeed =
			selectHasSufficientDataFor511Import(state) &&
			(state.rem.userHasModifiedIncludeInEventFeed && path !== 'eventType'
				? event.includeInEventFeed
				: EventTypeConfig[event.eventType]?.importTo511);

		void dispatch(
			getEventDescription({
				...event,
				geometry: geometry ? stringify(geometry as GeoJSONGeometry) : '',
			}),
		);

		if (state.routing.page !== AppSection.WZRT) {
			// eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
			void dispatch(setREMDraftEventProp('includeInEventFeed', includeInEventFeed));
		}
	}
	if (
		DRAFT_PROP_PATHS_THAT_INVALIDATE_DESCRIPTION.includes(path) &&
		!selectHasSufficientDataForDescription(state)
	) {
		dispatch(setREMEventDescription(''));

		if (state.routing.page !== AppSection.WZRT) {
			// eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
			void dispatch(setREMDraftEventProp('includeInEventFeed', false));
		}
	}
};

// handle any work zone form validation more complicated than a null check
const validateWorkZoneForm = (
	dispatch: ThunkDispatch<RootState, void, RootAction>,
	getState: () => RootState,
): void => {
	const draftEvent = getState().rem?.draftEvent;

	if (draftEvent == null) {
		return;
	}

	const errors: Record<string, string> = {};
	const existingErrors = { ...getState().rem.draftEventFormErrors };

	const {
		minRequiredQuantities,
		quantities,
		projectContacts,
		locationDetails,
		hasNonStandardLaneClosures,
	} = draftEvent;
	const { minimumContacts, requiredProjectContactTypes } = ConfigWZRT.form.projectContacts;

	if (
		isValidNumber(minRequiredQuantities) &&
		minRequiredQuantities > 0 &&
		(quantities == null || quantities.length < minRequiredQuantities)
	) {
		errors.quantities = `This work zone requires at least ${minRequiredQuantities} restrictions.`;
	}
	if (projectContacts == null || projectContacts.length < minimumContacts) {
		errors.projectContacts = `This work zone requires at least ${minimumContacts} project contacts.`;
	} else if (
		!requiredProjectContactTypes.every((requiredContactType) =>
			projectContacts.some((contact) => contact.contactType === requiredContactType),
		)
	) {
		errors.projectContacts = `This work zone requires the following contact types: ${requiredProjectContactTypes.join(
			', ',
		)}`;
	}

	if (
		(locationDetails?.district ?? []).some((districtName) =>
			ConfigWZRT.form.affectedLanes.districtsForNonStandardLaneClosures.includes(
				districtName as Districts,
			),
		) &&
		hasNonStandardLaneClosures === undefined
	) {
		errors.hasNonStandardLaneClosures =
			'Requests in this district must specify if they comply with the lane closure guidelines.';
	}

	projectContacts?.forEach((projectContact, index) => {
		if (projectContactIsValidForWorkZone(projectContact)) {
			return;
		}

		const { phoneNumber, email } = projectContact;
		const phoneNumberErrorKey = `projectContactPhoneNumber-${index}`;

		if (!isValidString(phoneNumber) || !isValidPhoneNumber(phoneNumber, 'US')) {
			errors[phoneNumberErrorKey] = isValidString(existingErrors[phoneNumberErrorKey])
				? 'Invalid phone number'
				: '';
		}
		if (email != null && email.trim() !== '' && !EMAIL_ADDRESS_REGEX.test(email)) {
			errors[`projectContactEmail-${index}`] = 'Invalid email address';
		}
	});

	dispatch(setREMDraftEventFormErrors(errors));
};

const updateDraftRampDetails = (
	dispatch: ThunkDispatch<RootState, void, RootAction>,
	getState: () => RootState,
	path: string,
	rampPk: unknown,
): void => {
	if (path === 'rampPk') {
		const route = getState().rem.draftEvent?.route;

		if (!route) {
			return;
		}

		const defaultLaneBlockageType = selectDefaultLaneBlockgeType(getState());

		if (isValidNumber(rampPk)) {
			const ramp = selectRampById(getState().rem, route, rampPk);
			const classification = selectRouteByName(getState(), route)?.classification;

			if (ramp == null) {
				return;
			}

			const geometry = ramp?.geometry as MultiLineString | undefined;
			let lon;
			let lat;

			if (geometry) {
				[lon, lat] = nearestPointOnLine(geometry, center(geometry)).geometry.coordinates;
			}

			const locationDetails: LocationDto = {
				region: ramp.region ? [ramp.region] : undefined,
				county: ramp.county ? [ramp.county] : undefined,
				district: ramp.district ? [ramp.district] : undefined,
				subdistrict: ramp.subdistrict ? [ramp.subdistrict] : undefined,
				latitude: lat,
				longitude: lon,
				classification,
				lanes: {},
			};

			const positiveLaneBlockage = getEmptyLaneBlockage();
			const negativeLaneBlockage = getEmptyLaneBlockage();

			if (ramp.positiveDirection === ramp.routeDirection) {
				if (ramp.rampType === 'entrance') {
					positiveLaneBlockage.entranceRampAffected = true;
				} else if (ramp.rampType === 'exit') {
					positiveLaneBlockage.exitRampAffected = true;
				}
			} else if (ramp.rampType === 'entrance') {
				negativeLaneBlockage.entranceRampAffected = true;
			} else if (ramp.rampType === 'exit') {
				negativeLaneBlockage.exitRampAffected = true;
			}

			dispatch(setDraftEventGeometry(geometry));
			// TODO: get rid of the circular relationships between setREMDraftEventProp and the functions it calls
			// TODO: make a new action to update multiple draft event props at once instead of having to make a separate call for each field
			// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
			dispatch(setREMDraftEventProp('lat', lat, false));
			// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
			dispatch(setREMDraftEventProp('lon', lon, false));
			// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
			dispatch(setREMDraftEventProp('locationDetails', locationDetails));
			dispatch(
				// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
				setREMDraftEventProp(
					'geometry',
					geometry ? stringify(geometry as GeoJSONGeometry) : '',
					false,
				),
			);
			// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
			dispatch(setREMDraftEventProp('startMileMarker', ramp.mileMarker));
			// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
			dispatch(setREMDraftEventProp('positiveLaneBlockage', positiveLaneBlockage));
			// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
			dispatch(setREMDraftEventProp('positiveLaneBlockageType', defaultLaneBlockageType));
			// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
			dispatch(setREMDraftEventProp('negativeLaneBlockage', negativeLaneBlockage));
			// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
			dispatch(setREMDraftEventProp('negativeLaneBlockageType', defaultLaneBlockageType));

			return;
		}

		dispatch(setDraftEventGeometry(undefined));
		// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
		dispatch(setREMDraftEventProp('lat', undefined, false));
		// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
		dispatch(setREMDraftEventProp('lon', undefined, false));
		// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
		dispatch(setREMDraftEventProp('locationDetails', undefined, false));
		// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
		dispatch(setREMDraftEventProp('geometry', undefined, false));
		// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
		dispatch(setREMDraftEventProp('startMileMarker', undefined, false));
		// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
		dispatch(setREMDraftEventProp('positiveLaneBlockage', getEmptyLaneBlockage()));
		// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
		dispatch(setREMDraftEventProp('positiveLaneBlockageType', defaultLaneBlockageType));
		// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
		dispatch(setREMDraftEventProp('negativeLaneBlockage', getEmptyLaneBlockage()));
		// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
		dispatch(setREMDraftEventProp('negativeLaneBlockageType', defaultLaneBlockageType));
	}
};

const updateDraftEventEmailGroupsByDistrict = (
	dispatch: ThunkDispatch<RootState, void, RootAction>,
	getState: () => RootState,
	path: string,
): void => {
	if (path === 'locationDetails') {
		const { draftEvent } = getState().rem;

		const district = draftEvent?.locationDetails?.district?.[0];

		if (!isValidNumber(draftEvent?.id) && district && !getState().rem.readOnlyMode) {
			const emailGroupInDistrict = getState().rem.emailGroupsRecipients?.emailGroups?.find(
				(emailGroup) =>
					emailGroup.name ===
					ConfigREMEventForm.notifications.districtToEmailGroup[
						district as keyof typeof ConfigREMEventForm.notifications.districtToEmailGroup
					],
			);

			if (
				emailGroupInDistrict &&
				!draftEvent?.emailGroupsRecipients?.emailGroups?.some(
					(emailGroup) => emailGroup.name === emailGroupInDistrict.name,
				)
			) {
				void dispatch(
					// eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
					setREMDraftEventProp('emailGroupsRecipients.emailGroups', [
						...(draftEvent?.emailGroupsRecipients?.emailGroups ?? []),
						emailGroupInDistrict,
					]),
				);
			}
		}
	}
};

export const setREMDraftEventProp =
	(
		path: DotNotationPath<RoadEventDto>,
		value: unknown,
		triggerUpdates = true,
	): ThunkActionRoot<void> =>
	(dispatch, getState): void => {
		let state = getState();
		const draftEvent = state.rem?.draftEvent;

		if (draftEvent !== undefined && (get(draftEvent, path) !== value || triggerUpdates)) {
			dispatch({
				type: REMActionType.SET_REM_DRAFT_EVENT_PROP,
				path,
				value,
			});

			if (triggerUpdates) {
				state = getState();

				void updateDraftLocationDetails(dispatch, getState, path);

				void updateDraftDescription(dispatch, getState, path);

				void tryRefreshDMSPreviews(dispatch, getState, path);

				void updateDraftIcon(dispatch, getState, path);

				updateDraftRampDetails(dispatch, getState, path, value);

				updateDraftEventEmailGroupsByDistrict(dispatch, getState, path);
			}

			if (getState().routing.page === AppSection.WZRT) {
				validateWorkZoneForm(dispatch, getState);
			}
		}
	};

export const clearDMSPreviews = () => {
	// refreshing DMS, clear out existing selected signs and previews
	store.dispatch(setDMSPreview(undefined));
};

export const setREMLoading = (loading?: boolean): SetREMLoading => ({
	type: REMActionType.SET_REM_LOADING,
	loading,
});

export const getEventFields =
	(): ThunkActionRoot<Promise<APIRequestReturn>> =>
	async (dispatch): Promise<APIRequestReturn> => {
		dispatch({ type: REMActionType.GET_REM_EVENT_FIELDS });
		const apiRequestReturn = await APIRequest(
			new Request(new URL(ConfigREMApi.fields(), APIConfig.endpointURLBase).href, {
				method: HTTPMethod.GET,
				headers: new Headers({
					...getAPIHeaders(),
				}),
			}),
		);

		try {
			if (apiRequestReturn.response?.ok) {
				const eventFields: RoadEventFieldDto =
					(await apiRequestReturn.response.json()) as RoadEventFieldDto;
				dispatch(setREMEventFields(eventFields));
			}
		} catch (error) {
			apiRequestReturn.apiError = APIError.ResponseUnparseable;
			if (DebuggingConfig.showConsoleLogs) {
				console.error(`error parsing event fields:`, error);
			}
		}
		return apiRequestReturn;
	};

export const setREMDraftEvent = (draftEvent?: DraftEvent): SetREMDraftEvent => ({
	type: REMActionType.SET_REM_DRAFT_EVENT,
	draftEvent,
});

export const setREMReadOnlyMode = (readOnlyMode: REMState['readOnlyMode']): SetREMReadOnlyMode => ({
	type: REMActionType.SET_REM_READ_ONLY_MODE,
	readOnlyMode,
});

export const startNewREMDraftEvent =
	(event?: REMState['draftEvent']): ThunkActionRoot<REMState['draftEvent']> =>
	(dispatch, getState): REMState['draftEvent'] => {
		const initDraftEvent = event ?? getEmptyDraftEvent();
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment

		if (ConfigREMEventForm.notifications.addAuthorAsRecipient) {
			const emailAddress = getState().user.authority?.email;
			const displayName = getState().user.authority?.name ?? '';

			if (isValidString(emailAddress)) {
				initDraftEvent.emailGroupsRecipients = {
					emailRecipients: [{ displayName, emailAddress }],
				};
			}
		}

		dispatch(setREMDraftEvent(initDraftEvent));
		return initDraftEvent;
	};

export const getREMEvents =
	(): ThunkActionRoot<Promise<APIRequestReturn>> =>
	async (dispatch): Promise<APIRequestReturn> => {
		dispatch({
			type: REMActionType.GET_REM_EVENTS,
		});
		const apiRequestReturn = await APIRequest(
			new Request(new URL(ConfigREMApi.events(), APIConfig.endpointURLBase).href, {
				method: HTTPMethod.GET,
				headers: new Headers({
					...getAPIHeaders(),
				}),
			}),
		);

		let events: RoadEventDto[];

		try {
			if (apiRequestReturn.response?.ok) {
				events = (await apiRequestReturn.response.json()) as RoadEventDto[];
				void dispatch(getREMEventIcons(events));
				dispatch(setREMEvents(events));
			}
		} catch (error) {
			apiRequestReturn.apiError = APIError.ResponseUnparseable;
			if (DebuggingConfig.showConsoleLogs) {
				console.error(`error parsing REM events:`, error);
			}
		}
		return apiRequestReturn;
	};

export const getREMEventImports =
	(): ThunkActionRoot<Promise<APIRequestReturn>> =>
	async (dispatch): Promise<APIRequestReturn> => {
		dispatch({
			type: REMActionType.GET_REM_EVENT_IMPORTS,
		});
		const apiRequestReturn = await APIRequest(
			new Request(new URL(ConfigREMApi.eventImports(), APIConfig.endpointURLBase).href, {
				method: HTTPMethod.GET,
				headers: new Headers({
					...getAPIHeaders(),
				}),
			}),
		);

		let events: RoadEventDto[];

		try {
			if (apiRequestReturn.response?.ok) {
				events = (await apiRequestReturn.response.json()) as RoadEventDto[];
				void dispatch(getREMEventIcons(events));
				dispatch({
					type: REMActionType.SET_REM_EVENT_IMPORTS,
					events,
				});
			}
		} catch (error) {
			apiRequestReturn.apiError = APIError.ResponseUnparseable;
			if (DebuggingConfig.showConsoleLogs) {
				console.error(`error parsing REM event imports:`, error);
			}
		}
		return apiRequestReturn;
	};

export const getWorkZoneRequests =
	(): ThunkActionRoot<Promise<APIRequestReturn>> =>
	async (dispatch): Promise<APIRequestReturn> => {
		dispatch({
			type: REMActionType.GET_WORK_ZONE_REQUESTS,
		});
		const apiRequestReturn = await APIRequest(
			new Request(new URL(ConfigREMApi.workZoneRequests, APIConfig.endpointURLBase).href, {
				method: HTTPMethod.GET,
				headers: new Headers({
					...getAPIHeaders(),
				}),
			}),
		);

		let events: RoadEventDto[];

		try {
			if (apiRequestReturn.response?.ok) {
				events = (await apiRequestReturn.response.json()) as RoadEventDto[];
				void dispatch(getREMEventIcons(events));
				dispatch({
					type: REMActionType.SET_WORK_ZONE_REQUESTS,
					events,
				});
			}
		} catch (error) {
			apiRequestReturn.apiError = APIError.ResponseUnparseable;
			if (DebuggingConfig.showConsoleLogs) {
				console.error(`error parsing REM event imports:`, error);
			}
		}
		return apiRequestReturn;
	};

export const forgetREMEvents =
	(eventIds: number[]): ThunkActionRoot<Promise<APIRequestReturn>> =>
	async (dispatch): Promise<APIRequestReturn> => {
		dispatch({
			type: REMActionType.FORGET_REM_EVENT,
		});
		const apiRequestReturn = await APIRequest(
			new Request(
				new Request(new URL(ConfigREMApi.forgetEvent(), APIConfig.endpointURLBase).href, {
					method: HTTPMethod.POST,
					headers: new Headers({
						...getAPIHeaders(),
					}),
					body: JSON.stringify(eventIds),
				}),
			),
		);

		if (apiRequestReturn.response?.ok) {
			void store.dispatch(getREMEventImports());

			dispatch(
				showMainBanner(
					NotificationErrorType.SUCCESS,
					{ title: `Event${eventIds.length > 1 ? 's' : ''} forgotten successfully!` },
					5000,
				),
			);
			void store.dispatch(navigate(`${ConfigCARSx.Pages[AppSection.IMPORTS].route}`));
		} else {
			dispatch(
				showMainBanner(
					NotificationErrorType.ERROR,
					{
						title: `Error forgetting event`,
						messages: apiRequestReturn.apiError ? [apiRequestReturn.apiError] : undefined,
					},
					5000,
				),
			);
		}

		return apiRequestReturn;
	};

export const takeoverREMEvent =
	(eventId: number): ThunkActionRoot<Promise<APIRequestReturn>> =>
	async (dispatch): Promise<APIRequestReturn> => {
		dispatch({
			type: REMActionType.TAKEOVER_REM_EVENT,
		});
		const apiRequestReturn = await APIRequest(
			new Request(
				new Request(new URL(ConfigREMApi.takeoverEvent(eventId), APIConfig.endpointURLBase).href, {
					method: HTTPMethod.POST,
					headers: new Headers({
						...getAPIHeaders(),
					}),
				}),
			),
		);

		if (apiRequestReturn.response?.ok) {
			void store.dispatch(getREMEventImports());

			dispatch(
				showMainBanner(
					NotificationErrorType.SUCCESS,
					{ title: `Event taken over successfully!` },
					5000,
				),
			);

			void store.dispatch(
				navigate(`${ConfigCARSx.Pages[AppSection.REM].route}/${REMView.event}/${eventId}`),
			);
		} else {
			dispatch(
				showMainBanner(
					NotificationErrorType.ERROR,
					{
						title: `Error taking over event`,
						messages: apiRequestReturn.apiError ? [apiRequestReturn.apiError] : undefined,
					},
					5000,
				),
			);
		}

		return apiRequestReturn;
	};

//	TODO: would this be more appropriate as a selector?
export const getREMEventFromEvents =
	(eventId: number): ThunkActionRoot<void> =>
	(dispatch, getState): void => {
		dispatch({ type: REMActionType.GET_REM_EVENT_FROM_EVENTS, eventId });
		const { events } = getState().rem;
		if (events) {
			const targetEvent = events?.find((event) => event.id === eventId);
			if (targetEvent) {
				const { draftEvent } = getState().rem;
				if (!draftEvent?.id) {
					dispatch(setREMDraftEvent(prepareServerEventForClient(targetEvent)));
				}
			} else {
				// eslint-disable-next-line no-lonely-if
				if (DebuggingConfig.showConsoleLogs) {
					console.warn(`unable to preload event data for event #${eventId}`);
				}
			}
		}
	};

export const getREMEvent =
	(eventId: number): ThunkActionRoot<Promise<APIRequestReturn>> =>
	async (dispatch): Promise<APIRequestReturn> => {
		dispatch({
			type: REMActionType.GET_REM_EVENT,
			eventId,
		});
		const apiRequestReturn = await APIRequest(
			new Request(new URL(ConfigREMApi.event(eventId), APIConfig.endpointURLBase).href, {
				method: HTTPMethod.GET,
				headers: new Headers({
					...getAPIHeaders(),
				}),
			}),
		);

		if (apiRequestReturn.response?.status === 404) {
			void dispatch(navigate(`${ConfigCARSx.Pages[AppSection.REM].route}/${REMView.event}/new`));
			return apiRequestReturn;
		}
		try {
			const event: RoadEventDto = (await apiRequestReturn.response?.clone().json()) as RoadEventDto;
			const clientDraftEvent = prepareServerEventForClient(event);

			dispatch(setREMDraftEvent(clientDraftEvent));
			void dispatch(getEventDescription(event));

			if (event.eventStatus === EventStatus.COMPLETED) {
				dispatch(setREMReadOnlyMode(true));
			}
		} catch (error) {
			apiRequestReturn.apiError = APIError.ResponseUnparseable;
			if (DebuggingConfig.showConsoleLogs) {
				console.error(`error parsing event #${eventId}:`, error);
			}
		}
		return apiRequestReturn;
	};

//	first check the event timeline, to see if there's a new entry -
//	and if there is, then pull the latest event record
export const refreshREMEvent =
	(eventId: number, triggerUpdates = false): ThunkActionRoot<Promise<APIRequestReturn>> =>
	async (dispatch, getState): Promise<APIRequestReturn> => {
		//	TODO: refactor the polling logic a bit to simplify sequence of requests
		dispatch({
			type: REMActionType.GET_REM_EVENT_TIMELINE,
			eventId,
		});
		const apiRequestReturn: APIRequestReturn = await APIRequest(
			new Request(new URL(ConfigREMApi.eventTimeline(eventId), APIConfig.endpointURLBase).href, {
				method: HTTPMethod.GET,
				headers: new Headers({
					...getAPIHeaders(),
				}),
			}),
		);

		let eventShouldPoll = true;
		let invalidTimeline = false;
		let eventTimeline: REMState['eventTimeline'] | APIErrorJSON;

		//	try parsing response

		try {
			eventTimeline = (await apiRequestReturn.response
				?.clone()
				.json()) as REMState['eventTimeline'];
		} catch (error) {
			apiRequestReturn.apiError = APIError.ResponseUnparseable;
			if (DebuggingConfig.showConsoleLogs) {
				console.error(`error parsing event timeline for event #${eventId}:`, error);
			}
			invalidTimeline = true;
		}

		//	is response useable?

		if (eventTimeline === undefined || isAPIErrorJSON(eventTimeline)) {
			apiRequestReturn.apiError = APIError.ServerError;
			if (DebuggingConfig.showConsoleLogs) {
				console.error(`server error fetching timeline for event #${eventId}:`, eventTimeline);
			}
			invalidTimeline = true;
		} else if (eventTimeline?.timeline?.entries?.length === 0) {
			apiRequestReturn.apiError = APIError.ResponseEmpty;
			if (DebuggingConfig.showConsoleLogs) {
				console.warn(`warning: timeline endpoint returned an empty result for event #${eventId}`);
			}
			invalidTimeline = true;
		}

		//	are there new timeline entries?

		const { eventTimeline: previousEventTimeline } = getState().rem;
		if (
			previousEventTimeline &&
			previousEventTimeline.timeline?.entries?.[0].timestamp ===
				(eventTimeline as RoadEventTimelineDto).timeline?.entries?.[0].timestamp
		) {
			eventShouldPoll = false; //	most recent timestamp is the same, so don't bother polling
		}

		//	set timeline data accordingly

		if (invalidTimeline) {
			dispatch(setREMEventTimeline(undefined));
		} else {
			dispatch(setREMEventTimeline(eventTimeline as REMState['eventTimeline'])); //	update state with new timeline data
		}

		//	and fetch updated event record if warranted

		const { unsavedDraftEvent } = getState().rem;

		if (
			eventShouldPoll &&
			!unsavedDraftEvent &&
			!apiRequestReturn.abortController?.signal.aborted
		) {
			await dispatch(getREMEvent(eventId));

			await dispatch(getREMEvents());

			const { draftEvent } = getState().rem;

			if (draftEvent && draftEvent.id) {
				await Promise.all([
					dispatch(getDMSforREMEvent(draftEvent)),
					handleNewLocationDetailsForREMEvent(
						dispatch,
						draftEvent.route,
						draftEvent.startMileMarker,
						draftEvent.endMileMarker,
						draftEvent.eventLocation as LocationTypes | undefined,
						triggerUpdates,
					),
				]);
			}
		}

		return apiRequestReturn;
	};

export const saveREMEventAttachments =
	(
		eventId: number,
		attachments: RoadEventAttachmentDto[],
	): ThunkActionRoot<Promise<APIRequestReturn | void>> =>
	async (dispatch, getState): Promise<APIRequestReturn | void> => {
		const fileMap = getState().rem.draftEventAttachmentsFileMap;

		if (
			!FormSectionConfig.ATTACHMENTS.active ||
			(attachments.length === 0 && Object.values(fileMap).length === 0)
		) {
			return undefined;
		}

		const formData = new FormData();

		Object.entries(fileMap).forEach((entry) => {
			const index = entry[0];
			const file = entry[1];

			if (attachments[parseInt(index, 10)]?.uploadBytes != null) {
				formData.append('files', file);
				formData.append('fileIndices', index);
			}
		});

		const newAttachments = attachments.map((attachment) => ({
			...attachment,
			uploadBytes: undefined,
		}));
		formData.append('attachmentDtos', JSON.stringify(newAttachments));

		const url = new URL(ConfigREMApi.attachments(eventId), APIConfig.endpointURLBase);

		const attachmentsRequestReturn = await APIRequest(
			new Request(url.href, {
				method: HTTPMethod.POST,
				headers: {
					Authorization: Authorization.JWT ?? '',
					Accept: 'application/json',
				},
				body: formData,
			}),
		);

		if (!attachmentsRequestReturn.response?.ok) {
			dispatch(
				showMainBanner(NotificationErrorType.ERROR, {
					title: 'Error occurred when attempting to save attachments to event',
				}),
			);
		} else {
			const attachments =
				(await attachmentsRequestReturn.response?.json()) as RoadEventAttachmentDto[];
			dispatch(setREMDraftEventAttachments(attachments));
			dispatch(setREMDraftEventAttachmentsFileMap({}));
		}

		return attachmentsRequestReturn;
	};

export const createREMEvent =
	(draftEvent: DraftEvent): ThunkActionRoot<Promise<APIRequestReturn>> =>
	async (dispatch): Promise<APIRequestReturn> => {
		dispatch({
			type: REMActionType.CREATE_REM_EVENT,
			draftEvent,
		});
		dispatch(addItem('api', 'rem-create-event'));
		dispatch(setREMLoading(true));

		if (userHasPermission(UserPermissions.REM_CAN_CREATE_EVENTS_LIMITED)) {
			draftEvent.includeInEventFeed = false;
		}

		const preparedDraftEvent = prepareDraftEventForServer(draftEvent);
		const apiRequestReturn = await APIRequest(
			new Request(
				new Request(new URL(ConfigREMApi.createOrUpdate(), APIConfig.endpointURLBase).href, {
					method: HTTPMethod.POST,
					headers: new Headers({
						...getAPIHeaders(),
					}),
					body: JSON.stringify(preparedDraftEvent),
				}),
			),
		);

		if (apiRequestReturn.response?.status !== 200) {
			dispatch(setREMLoading(false));

			const apiError = (await apiRequestReturn.response?.json()) as APIErrorJSON;
			dispatch(
				showMainBanner(
					NotificationErrorType.ERROR,
					{
						title: `Error creating incident.`,
						messages: [
							`Server status code: ${apiError.status}`,
							`Error message: ${apiError.message}`,
						],
					},
					5000,
				),
			);
			return apiRequestReturn;
		}

		let roadwayEventMessages: RoadwayEventMessagesDto;
		try {
			roadwayEventMessages = (await apiRequestReturn.response
				?.clone()
				.json()) as RoadwayEventMessagesDto;
		} catch (error) {
			dispatch(
				showMainBanner(
					NotificationErrorType.ERROR,
					{ title: `Error creating incident: `, messages: [`Error message: ${error as string}`] },
					5000,
				),
			);
			dispatch(removeItem('api', 'rem-create-event'));
			dispatch(setREMLoading(false));
			return apiRequestReturn;
		}

		const { eventDto, eventMessages } = roadwayEventMessages;

		dispatch(setREMDraftEvent(prepareServerEventForClient(eventDto)));
		if (eventMessages) {
			const eventDMSPreviewMessages = prepareDMSMessagesForClient(
				eventMessages as unknown as PreviewDto[],
			);
			dispatch(setDMSPreview(eventDMSPreviewMessages));
			const eventDMSSelectedSigns =
				getInitialSignSelectionStateFromDMSMessages(eventDMSPreviewMessages);
			void dispatch(setREMDraftEventProp('selectedSigns', eventDMSSelectedSigns));
		}
		let signs: SignDto[] = [];
		if (draftEvent.selectedSigns) {
			// TODO: call api/sign instead, passing in sign-ids as a query parameter
			//		 remove this getSignsByIds actions
			signs = await dispatch(
				getSignsByIds(draftEvent.selectedSigns.map((selection) => selection.signId)),
			);
		}
		const erroredSigns = signs.filter((sign) => sign.currentMessageState === QueueState.ERROR);
		const successfulSigns = signs.filter((sign) => sign.currentMessageState === QueueState.SENT);

		dispatch({ type: REMActionType.REM_EVENT_UPDATED });
		dispatch(
			showMainBanner(
				NotificationErrorType.SUCCESS,
				{
					title: `Incident #${eventDto.id} created successfully`,
					messages: successfulSigns.map(
						(sign) => `successfully posted to DMS ${sign.name ?? sign.id}`,
					),
				},
				5000,
			),
		);
		if (erroredSigns.length) {
			dispatch(
				showMainBanner(
					NotificationErrorType.ERROR,
					{
						title: `Error posting messages to signs`,
						messages: erroredSigns.map(
							(sign) => `failed to post message to DMS ${sign.name ?? sign.id}`,
						),
					},
					5000,
				),
			);
		}

		dispatch(removeItem('api', 'rem-create-event'));
		await dispatch(refreshREMEvent(eventDto.id));
		await dispatch(getEventDescription(eventDto));
		await dispatch(
			saveREMEventAttachments(eventDto.id, store.getState().rem.draftEventAttachments),
		);

		dispatch(setREMLoading(false));

		return apiRequestReturn;
	};

export const updateREMEvent =
	(
		draftEvent: DraftEvent,
		redirect: string | false = false,
	): ThunkActionRoot<Promise<APIRequestReturn>> =>
	async (dispatch, getState): Promise<APIRequestReturn> => {
		dispatch({
			type: REMActionType.UPDATE_REM_EVENT,
			draftEvent,
		});
		dispatch(addItem('api', 'rem-update-event'));
		dispatch(setREMLoading(true));

		const preparedDraftEvent = prepareDraftEventForServer(draftEvent);
		const now = new Date().getTime();

		if (preparedDraftEvent.eventStatus === EventStatus.COMPLETED) {
			preparedDraftEvent.respondingUnits = (
				preparedDraftEvent.respondingUnits as RespondingUnitDto[]
			)?.map((unit) => ({
				...unit,
				assigned: unit.assigned ?? now,
				arrived: unit.arrived ?? now,
				completed: unit.completed ?? now,
			}));
		}

		const { page } = getState().routing;
		const endpoint =
			page === AppSection.WZRT ? ConfigREMApi.workZoneRequests : ConfigREMApi.createOrUpdate();

		if (page === AppSection.WZRT) {
			preparedDraftEvent.projectContacts = preparedDraftEvent.projectContacts?.filter(
				(contact) => !projectContactIsEmptyAndOptional(contact),
			);
		}

		const apiRequestReturn = await APIRequest(
			new Request(
				new Request(new URL(endpoint, APIConfig.endpointURLBase).href, {
					method: HTTPMethod.POST,
					headers: new Headers({
						...getAPIHeaders(),
					}),
					body: JSON.stringify(preparedDraftEvent),
				}),
			),
		);

		if (apiRequestReturn.response?.status === HttpStatusCode.CONFLICT) {
			dispatch(
				showMainBanner(
					NotificationErrorType.ERROR,
					{
						title: `Error updating incident: `,
						messages: [`A more recent version of this form has been submitted by another user.`],
					},
					5000,
				),
			);
			dispatch(setREMLoading(false));
			return apiRequestReturn;
		}

		if (apiRequestReturn.response?.status !== 200) {
			dispatch(setREMLoading(false));

			const apiError = (await apiRequestReturn.response?.json()) as APIErrorJSON;
			dispatch(
				showMainBanner(
					NotificationErrorType.ERROR,
					{
						title: `Error updating incident.`,
						messages: [
							`Server status code: ${apiError.status}`,
							`Error message: ${apiError.message}`,
						],
					},
					5000,
				),
			);
			return apiRequestReturn;
		}

		let roadwayEventMessages: RoadwayEventMessagesDto;
		try {
			roadwayEventMessages = (await apiRequestReturn.response
				?.clone()
				.json()) as RoadwayEventMessagesDto;
		} catch (error) {
			dispatch(
				showMainBanner(
					NotificationErrorType.ERROR,
					{ title: `Error updating incident: `, messages: [`Error message: ${error as string}`] },
					5000,
				),
			);
			dispatch(setREMLoading(false));
			dispatch(removeItem('api', 'rem-update-event'));
			return apiRequestReturn;
		}

		const { eventDto, eventMessages } = roadwayEventMessages;

		// re-prioritize messages if the refresh button was clicked, otherwise update the messages normally
		if (store.getState().rem.shouldRefreshEventMessages && draftEvent.id) {
			await dispatch(prioritizeMessages(draftEvent.id, true));
		} else if (eventMessages) {
			const eventDMSPreviewMessages = prepareDMSMessagesForClient(
				eventMessages as unknown as PreviewDto[],
			);
			dispatch(setDMSPreview(eventDMSPreviewMessages));
			const eventDMSSelectedSigns =
				getInitialSignSelectionStateFromDMSMessages(eventDMSPreviewMessages);
			void dispatch(setREMDraftEventProp('selectedSigns', eventDMSSelectedSigns));
		}

		await dispatch(
			saveREMEventAttachments(eventDto.id, store.getState().rem.draftEventAttachments),
		);

		if (redirect) {
			dispatch(
				showMainBanner(
					NotificationErrorType.SUCCESS,
					{ title: `Incident #${eventDto.id} completed successfully` },
					5000,
				),
			);
			void dispatch(
				navigateBack(redirect, [`${ConfigCARSx.Pages[AppSection.REM].route}/${REMView.event}/new`]),
			);
			dispatch(setREMLoading(false));
			return apiRequestReturn;
		}

		dispatch(setREMDraftEvent(prepareServerEventForClient(eventDto)));

		dispatch({ type: REMActionType.REM_EVENT_UPDATED });
		dispatch(
			showMainBanner(
				NotificationErrorType.SUCCESS,
				{ title: `Incident #${eventDto.id} updated successfully` },
				5000,
			),
		);

		if (eventDto.eventStatus !== EventStatus.COMPLETED) {
			await dispatch(refreshREMEvent(eventDto.id));
			await dispatch(getEventDescription(eventDto));
		}

		dispatch(setREMLoading(false));
		dispatch(removeItem('api', 'rem-update-event'));
		return apiRequestReturn;
	};

export const showREMEventSection = (
	section: keyof typeof REM_FORM_SECTIONS,
): ShowREMEventSection => ({
	type: REMActionType.SHOW_REM_EVENT_SECTION,
	section,
});

export const hideREMEventSection = (
	section: keyof typeof REM_FORM_SECTIONS,
): HideREMEventSection => ({
	type: REMActionType.HIDE_REM_EVENT_SECTION,
	section,
});

export const updateWorkZoneStatus = async (
	roadwayEventId: number,
	actionType: PublishActionType,
	notes?: string,
	successMessage?: string,
	errorMessage?: string,
): Promise<void> => {
	store.dispatch(setREMLoading(true));

	if (!userCanPerformWorkZoneAction(actionType)) {
		store.dispatch(
			showMainBanner(
				NotificationErrorType.ERROR,
				{
					title: `User does not have permission to ${actionType.toLowerCase()} work zone requests`,
				},
				5000,
			),
		);

		return;
	}

	const publishAction: PublishActionDto = {
		roadwayEventId,
		actionType,
		notes,
	};

	const apiRequestReturn = await APIRequest(
		new Request(new URL(ConfigREMApi.getWorkZoneRequests, APIConfig.endpointURLBase).href, {
			method: HTTPMethod.POST,
			headers: new Headers({
				...getAPIHeaders(),
			}),
			body: JSON.stringify(publishAction),
		}),
	);

	if (apiRequestReturn.response?.status !== 200 && errorMessage) {
		const apiError = (await apiRequestReturn.response?.json()) as APIErrorJSON;
		store.dispatch(
			showMainBanner(
				NotificationErrorType.ERROR,
				{
					title: errorMessage,
					messages: [
						`Server status code: ${apiError.status}`,
						`Error message: ${apiError.message}`,
					],
				},
				5000,
			),
		);
		return;
	}

	if (successMessage) {
		store.dispatch(
			showMainBanner(
				NotificationErrorType.SUCCESS,
				{
					title: successMessage,
				},
				5000,
			),
		);
	}

	await store.dispatch(refreshREMEvent(roadwayEventId));

	store.dispatch(setREMLoading(false));
};
